import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductStock Component
 * 
 * Manages product stock levels with a split layout:
 * - Left sidebar for product selection
 * - Right panel for stock details with dynamic columns
 * - Modal-based editing
 */
const ProductStock = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productStock, setProductStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    warehouse_id: '',
    location_id: '',
    quantity_on_hand: 0,
    quantity_reserved: 0,
    quantity_available: 0,
    reorder_point: 0,
    reorder_quantity: 0,
    max_stock_level: null,
    lot_number: '',
    serial_number: '',
    batch_number: '',
    expiration_date: '',
    manufacture_date: '',
    status: 'available'
  });

  useEffect(() => {
    if (organization) {
      const loadData = async () => {
        loadColumnSettings();
        await loadProducts();
        await loadWarehouses();
      };
      loadData();
    }
  }, [organization]);

  useEffect(() => {
    if (selectedProduct) {
      loadProductStock(selectedProduct);
    }
  }, [selectedProduct]);

  // Handle panel resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(200, Math.min(600, e.clientX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`productStockColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`productStockColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await inventoryApi.getWarehouses(organization);
      const warehousesData = Array.isArray(response) ? response : (response.data || []);
      setWarehouses(warehousesData);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadProductStock = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProductStock(organization, productId);
      const stockData = Array.isArray(response) ? response : (response.data || []);
      setProductStock(stockData);

      // Generate dynamic columns based on the first stock record
      if (stockData.length > 0) {
        const saved = localStorage.getItem(`productStockColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = stockData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load product stock');
      setProductStock([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    saveColumnSettings(columnSettings, newSort);
  };

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`productStockColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (productStock.length > 0) {
      const firstItem = productStock[0];
      const columns = Object.keys(firstItem)
        .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
        .map((key, index) => ({
          key,
          label: formatColumnLabel(key),
          visible: true,
          order: index
        }));
      setTableColumns(columns);
    }
  };

  // Format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle dates
    if (key.includes('date') || key.includes('Date')) {
      const date = new Date(value);
      const isExpired = date < new Date();
      const isExpiringSoon = date < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      const dateStr = date.toLocaleDateString();
      
      if (isExpired) {
        return <span style={{ color: 'var(--pico-del-color)', fontWeight: 600 }}>{dateStr}</span>;
      } else if (isExpiringSoon) {
        return <span style={{ color: 'var(--pico-ins-color)', fontWeight: 600 }}>{dateStr}</span>;
      }
      return dateStr;
    }
    
    // Handle quantities
    if (key.includes('quantity') || key.includes('Quantity') || key.includes('point') || key.includes('Point')) {
      return parseFloat(value).toFixed(2);
    }
    
    // Handle status
    if (key === 'status') {
      return (
        <span className={`pm-badge ${value === 'available' ? 'valid' : value === 'expired' || value === 'damaged' ? 'error' : ''}`}>
          {(value || 'available').toUpperCase()}
        </span>
      );
    }
    
    // Handle warehouse_id
    if (key === 'warehouse_id' || key === 'warehouseId') {
      return getWarehouseName(value);
    }
    
    return String(value);
  };

  const handleOpenModal = (stock = null) => {
    setIsModalOpen(true);
    if (stock) {
      setEditingId(stock.id);
      setEditFormData({
        warehouse_id: stock.warehouse_id || stock.warehouseId || '',
        location_id: stock.location_id || stock.locationId || '',
        quantity_on_hand: stock.quantity_on_hand || stock.quantityOnHand || 0,
        quantity_reserved: stock.quantity_reserved || stock.quantityReserved || 0,
        quantity_available: stock.quantity_available || stock.quantityAvailable || 0,
        reorder_point: stock.reorder_point || stock.reorderPoint || 0,
        reorder_quantity: stock.reorder_quantity || stock.reorderQuantity || 0,
        max_stock_level: stock.max_stock_level || stock.maxStockLevel || null,
        lot_number: stock.lot_number || stock.lotNumber || '',
        serial_number: stock.serial_number || stock.serialNumber || '',
        batch_number: stock.batch_number || stock.batchNumber || '',
        expiration_date: stock.expiration_date || stock.expirationDate || '',
        manufacture_date: stock.manufacture_date || stock.manufactureDate || '',
        status: stock.status || 'available'
      });
    } else {
      setEditingId(null);
      setEditFormData({
        warehouse_id: '',
        location_id: '',
        quantity_on_hand: 0,
        quantity_reserved: 0,
        quantity_available: 0,
        reorder_point: 0,
        reorder_quantity: 0,
        max_stock_level: null,
        lot_number: '',
        serial_number: '',
        batch_number: '',
        expiration_date: '',
        manufacture_date: '',
        status: 'available'
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setEditFormData({
      warehouse_id: '',
      location_id: '',
      quantity_on_hand: 0,
      quantity_reserved: 0,
      quantity_available: 0,
      reorder_point: 0,
      reorder_quantity: 0,
      max_stock_level: null,
      lot_number: '',
      serial_number: '',
      batch_number: '',
      expiration_date: '',
      manufacture_date: '',
      status: 'available'
    });
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    try {
      setError(null);
      const dataToSave = {
        ...editFormData,
        product_id: selectedProduct,
        organization
      };
      
      if (editingId) {
        await inventoryApi.updateProductStock(editingId, editFormData);
      } else {
        await inventoryApi.createProductStock(dataToSave);
      }
      await loadProductStock(selectedProduct);
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save stock record');
    }
  };

  const handleDelete = async (stockId) => {
    if (!window.confirm('Are you sure you want to delete this stock record?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.deleteProductStock(stockId);
      await loadProductStock(selectedProduct);
    } catch (err) {
      setError(err.message || 'Failed to delete stock record');
    }
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'Unknown';
  };

  const getTotalStock = () => {
    return productStock.reduce((sum, stock) => 
      sum + (parseFloat(stock.quantity_on_hand || stock.quantityOnHand || 0)), 0
    );
  };

  const getTotalAvailable = () => {
    return productStock.reduce((sum, stock) => 
      sum + (parseFloat(stock.quantity_available || stock.quantityAvailable || 0)), 0
    );
  };

  const getTotalReserved = () => {
    return productStock.reduce((sum, stock) => 
      sum + (parseFloat(stock.quantity_reserved || stock.quantityReserved || 0)), 0
    );
  };

  if (loading && products.length === 0) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_products')}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="pm-page-card">
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
        <button className="pm-btn-primary" onClick={() => { loadProducts(); loadWarehouses(); }}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  // Filter products for the side list
  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term) ||
      product.SKU?.toLowerCase().includes(term)
    );
  });

  const selectedProductData = products.find(p => p.id === selectedProduct);

  // Filter stock based on search term
  const filteredStock = productStock.filter(stock => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(stock).some(value =>
      String(value).toLowerCase().includes(term)
    );
  });

  // Sort stock
  const sortedStock = [...filteredStock].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const visibleColumns = tableColumns.filter(col => col.visible);

  return (
    <div className="pm-page-card pm-split-layout" style={{ '--sidebar-width': `${sidebarWidth}px` }}>
      {/* Product Selection Sidebar */}
      <div className="pm-sidebar">
        <h3>{t('inventory.products_heading')}</h3>
        <div className="pm-sidebar-search">
          <input
            type="search"
            placeholder={t('inventory.search_products_placeholder')}
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-product-list-wrap">
          {filteredProducts.length === 0 ? (
            <div className="pm-empty-state compact">
              <p>{t('inventory.no_products_found')}</p>
            </div>
          ) : (
            <ul className="pm-product-list">
              {filteredProducts.map(product => (
                <li 
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`pm-product-list-item ${selectedProduct === product.id ? 'selected' : ''}`}
                >
                  <div className="pm-product-list-name">{product.name}</div>
                  <div className="pm-product-list-sku">
                    {t('inventory.sku_prefix')} {product.sku || product.SKU || 'N/A'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Resizer */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className={`pm-resizer ${isResizing ? 'resizing' : ''}`}
      >
        <div className="pm-resizer-icon">⋮</div>
      </div>

      {/* Main Content Area */}
      <div className="pm-main-panel">
        <div className="pm-header">
          <h3 className="pm-header-title">
            {t('inventory.product_stock_title')}
            {selectedProductData && (
              <span className="pm-context-title">
                {t('inventory.product_variants_for')} {selectedProductData.name}
              </span>
            )}
          </h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()} disabled={!selectedProduct}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_stock_location_button')}</span>
          </button>
        </div>

        {error && (
          <div className="pm-error-banner">
            <span aria-hidden="true">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {selectedProduct && productStock.length > 0 && (
          <div className="pm-metric-row">
            <div className="pm-metric-pill">
              <span className="pm-metric-pill-label">{t('inventory.total_stock_on_hand')}</span>
              <span className="pm-metric-pill-value">{getTotalStock()}</span>
            </div>
            <div className="pm-metric-pill">
              <span className="pm-metric-pill-label">{t('inventory.total_available')}</span>
              <span className="pm-metric-pill-value available">{getTotalAvailable()}</span>
            </div>
            <div className="pm-metric-pill">
              <span className="pm-metric-pill-label">{t('inventory.total_reserved')}</span>
              <span className="pm-metric-pill-value reserved">{getTotalReserved()}</span>
            </div>
          </div>
        )}

        <div className="pm-toolbar">
          <div className="pm-search-wrap">
            <input
              type="search"
              placeholder={t('inventory.search_stock_records_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!selectedProduct ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
            <p>{t('inventory.select_product_stock_message')}</p>
          </div>
        ) : productStock.length === 0 && !loading ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">📊</div>
            <h3>{t('inventory.no_stock_records_found')}</h3>
            <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
              <span aria-hidden="true">➕</span>
              {t('inventory.add_first_stock_record_button')}
            </button>
          </div>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table" role="grid">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>
                    <div className="pm-header-content">
                      {tableColumns.filter(c => !c.visible).length > 0 && (
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === 'menu' ? null : 'menu')}
                          title={t('inventory.show_hidden_columns')}
                        >
                          ⊕
                        </button>
                      )}
                      <div className="pm-header-label">{t('inventory.actions_header')}</div>
                    </div>
                    {showColumnMenu === 'menu' && (
                      <div className="pm-column-menu">
                        <div className="menu-header">{t('inventory.hidden_columns_label')}</div>
                        {tableColumns.filter(col => !col.visible).map(col => (
                          <button key={col.key} onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.show_column')} {col.label}
                          </button>
                        ))}
                        <hr />
                        <button onClick={() => {
                          resetColumnSettings();
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.reset_columns')}
                        </button>
                      </div>
                    )}
                  </th>
                  {visibleColumns.map((col, index) => (
                    <th key={col.key} className="pm-sortable-header">
                      <div className="pm-header-content">
                        <div className="pm-header-controls">
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnUp(col.key)}
                            disabled={index === 0}
                            title={t('inventory.move_left_tooltip')}
                          >
                            ◀
                          </button>
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnDown(col.key)}
                            disabled={index === visibleColumns.length - 1}
                            title={t('inventory.move_right_tooltip')}
                          >
                            ▶
                          </button>
                          <button
                            className="pm-column-btn visibility-btn"
                            onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                            title={t('inventory.column_options_tooltip')}
                          >
                            ⋮
                          </button>
                        </div>
                        <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span className="pm-sort-indicator">
                              {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </div>
                      </div>
                      {showColumnMenu === col.key && (
                        <div className="pm-column-menu">
                          <button onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.hide_column')}
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      <div className="pm-skeleton" aria-busy="true">{t('inventory.loading')}</div>
                    </td>
                  </tr>
                ) : (
                  sortedStock.map((stock) => (
                    <tr key={stock.id}>
                      <td>
                        <div className="pm-row-actions">
                          <button className="pm-row-btn edit" onClick={() => handleOpenModal(stock)} title={t('inventory.edit_tooltip')}>
                            <span aria-hidden="true">✏️</span>
                          </button>
                          <button className="pm-row-btn delete" onClick={() => handleDelete(stock.id)} title={t('inventory.delete_tooltip')}>
                            <span aria-hidden="true">🗑️</span>
                          </button>
                        </div>
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>
                          {formatCellValue(stock[col.key], col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_stock_record_title') : t('inventory.add_stock_record_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                <div className="pm-form-group">
                  <label htmlFor="stock-warehouse">{t('inventory.warehouse_label')}</label>
                  <select
                    id="stock-warehouse"
                    value={editFormData.warehouse_id}
                    onChange={(e) => setEditFormData({ ...editFormData, warehouse_id: e.target.value })}
                    required
                  >
                    <option value="">{t('inventory.select_warehouse_option')}</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-location">{t('inventory.location_id_label')}</label>
                  <input
                    id="stock-location"
                    type="text"
                    value={editFormData.location_id}
                    onChange={(e) => setEditFormData({ ...editFormData, location_id: e.target.value })}
                    placeholder={t('inventory.location_id_placeholder')}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-qoh">{t('inventory.quantity_on_hand_label')}</label>
                  <input
                    id="stock-qoh"
                    type="number"
                    step="0.01"
                    value={editFormData.quantity_on_hand}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity_on_hand: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-reserved">{t('inventory.quantity_reserved_label')}</label>
                  <input
                    id="stock-reserved"
                    type="number"
                    step="0.01"
                    value={editFormData.quantity_reserved}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity_reserved: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-available">{t('inventory.quantity_available_label')}</label>
                  <input
                    id="stock-available"
                    type="number"
                    step="0.01"
                    value={editFormData.quantity_available}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity_available: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-reorder-point">{t('inventory.reorder_point_label')}</label>
                  <input
                    id="stock-reorder-point"
                    type="number"
                    step="0.01"
                    value={editFormData.reorder_point}
                    onChange={(e) => setEditFormData({ ...editFormData, reorder_point: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-reorder-qty">{t('inventory.reorder_quantity_label')}</label>
                  <input
                    id="stock-reorder-qty"
                    type="number"
                    step="0.01"
                    value={editFormData.reorder_quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, reorder_quantity: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-max">{t('inventory.max_stock_level_label')}</label>
                  <input
                    id="stock-max"
                    type="number"
                    step="0.01"
                    value={editFormData.max_stock_level || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, max_stock_level: e.target.value })}
                  />
                </div>

                <div className="pm-subsection-title">{t('inventory.tracking_information_heading')}</div>

                <div className="pm-form-group">
                  <label htmlFor="stock-lot">{t('inventory.lot_number_label')}</label>
                  <input
                    id="stock-lot"
                    type="text"
                    value={editFormData.lot_number}
                    onChange={(e) => setEditFormData({ ...editFormData, lot_number: e.target.value })}
                    placeholder={t('inventory.lot_number_placeholder')}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-serial">{t('inventory.serial_number_label')}</label>
                  <input
                    id="stock-serial"
                    type="text"
                    value={editFormData.serial_number}
                    onChange={(e) => setEditFormData({ ...editFormData, serial_number: e.target.value })}
                    placeholder={t('inventory.serial_number_placeholder')}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-batch">{t('inventory.batch_number_label')}</label>
                  <input
                    id="stock-batch"
                    type="text"
                    value={editFormData.batch_number}
                    onChange={(e) => setEditFormData({ ...editFormData, batch_number: e.target.value })}
                    placeholder={t('inventory.batch_number_placeholder')}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-mfg">{t('inventory.manufacture_date_label')}</label>
                  <input
                    id="stock-mfg"
                    type="date"
                    value={editFormData.manufacture_date}
                    onChange={(e) => setEditFormData({ ...editFormData, manufacture_date: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-exp">{t('inventory.expiration_date_label')}</label>
                  <input
                    id="stock-exp"
                    type="date"
                    value={editFormData.expiration_date}
                    onChange={(e) => setEditFormData({ ...editFormData, expiration_date: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="stock-status">{t('inventory.status_label')}</label>
                  <select
                    id="stock-status"
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  >
                    <option value="available">{t('inventory.status_available')}</option>
                    <option value="reserved">{t('inventory.status_reserved')}</option>
                    <option value="quarantine">{t('inventory.status_quarantine')}</option>
                    <option value="damaged">{t('inventory.status_damaged')}</option>
                    <option value="expired">{t('inventory.status_expired')}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal} type="button">
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave} type="button">
                {editingId ? t('inventory.update_category_button') : t('inventory.create_category_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductStock;
